import { currentAuthSession } from "./BCPAuthProvider";
import AuthObject from "./objects/BCPAuthObject";
import {
  getData,
  postData,
  setCachedItems,
  urlToData
} from "../utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import {
  getCookie,
  getLocalStorage,
  removeCookie,
  removeLocalStorage,
  setCookie,
  setLocalStorage
} from "../layout/cookies/CookieFunctions";
import { datadogLogs } from "@datadog/browser-logs";
import tokenRefreshQueue from "../utilities/TokenRefreshQueue";

export async function signin(credentials, doReducer, options) {
  if (!credentials) {
    let err = new Error("missing credentials");
    err.code = 409;
    throw err;
  }

  if (!credentials?.username && !credentials?.email) {
    let err = new Error("missing credentials.username or credentials.email");
    err.code = 409;
    throw err;
  }

  let requiredFields = ["password"];
  for (let requiredField of requiredFields) {
    if (!credentials?.[requiredField]) {
      let err = new Error(`missing credentials.${requiredField}`);
      err.code = 409;
      throw err;
    }
  }

  const cookieVal = getCookie("CookieConsent");

  const cookieObj = {
    consented: true,
    essential: true,
    analytical: cookieVal?.analytical || false,
    performance: cookieVal?.performance || false,
    informational: cookieVal?.informational || false
  };

  let authObject = new AuthObject({
    cookieConsent: cookieObj
  });

  if (cookieObj?.analytical) {
    datadogLogs.setTrackingConsent("granted");
  }

  let setProcessing = options?.setProcessing;
  try {
    if (setProcessing) {
      setProcessing(true);
    }

    let apiRequest = await postData(
      "/users/signin",
      {
        body: {
          username: credentials?.username || credentials?.email,
          password: credentials?.password
        }
      },
      "APIUnsecured"
    );

    authObject.syncTokens({
      idTokenString: apiRequest?.idToken,
      accessTokenString: apiRequest?.accessToken,
      refreshTokenString: apiRequest?.refreshToken
    });

    setCookie("CookieConsent", JSON.stringify(cookieObj));

    doReducer(authObject);
  } catch (err) {
    throw err;
  }

  return authObject;
}

export async function signup(credentials, doReducer, options) {
  if (!credentials) {
    let err = new Error("missing credentials");
    err.code = 409;
    throw err;
  }
  let requiredFields = ["email", "firstName", "lastName", "password"];

  for (let requiredField of requiredFields) {
    if (!credentials?.[requiredField]) {
      let err = new Error(`missing credentials.${requiredField}`);
      err.code = 409;
      throw err;
    }
  }

  var body = {
    email: credentials.email.trim(),
    firstName: credentials.firstName.trim(),
    lastName: credentials.lastName.trim(),
    password: credentials.password
  };

  if (credentials?.source?.primary) {
    body.source = { primary: credentials?.source.primary };
    if (credentials?.source.secondary) {
      body.source.secondary = credentials?.source.secondary;
    }
  }

  var newUser;
  let setProcessing = options?.setProcessing;
  try {
    if (setProcessing) {
      setProcessing(true);
    }

    newUser = await postData(
      "/users",
      {
        body: body
      },
      "APIUnsecured"
    );
  } catch (err) {
    throw err;
  }

  return newUser;
}

export async function signout(doReducer, options) {
  var currentSession;
  try {
    currentSession = await currentAuthSession({ ignoreExpired: true });
  } catch (err) {
    throw err;
  }

  if (!currentSession?.refreshTokenString) {
    throw new Error("missing refreshToken");
  }

  let setProcessing = options?.setProcessing;
  let navigate = options?.navigate;
  try {
    if (setProcessing) {
      setProcessing(true);
    }

    await postData(
      "/oauth/logout",
      {
        token: currentSession.refreshTokenString,
        grant_type: "refresh_token"
      },
      "API",
      null,
      getEnvironmentVariable("API")
    );
  } catch (err) {
    throw err;
  }

  currentSession?.removeTokens();
  datadogLogs.clearAccount();

  removeCookie("CookieConsent");
  var getCachedItems = getLocalStorage("CachedItems");
  var cachedItems = getCachedItems ? JSON.parse(getCachedItems) : {};
  try {
    Object.keys(cachedItems).forEach((cachedKey) => {
      if (cachedItems[cachedKey]) {
        removeLocalStorage(cachedKey);
        delete cachedItems[cachedKey];
      }
    });
    setLocalStorage("CachedItems", JSON.stringify(cachedItems));

    removeLocalStorage("cartState");
  } catch (err) {}

  doReducer(currentSession);

  if (setProcessing) {
    setProcessing(false);
  }

  if (options?.redirectUrl) {
    window.location.assign(options?.redirectUrl);
    return;
  }
  if (navigate) {
    navigate("/");
  } else {
    window.location.assign("/");
  }

  return currentSession;
}

export async function refreshTokens() {
  try {
    await tokenRefreshQueue.refreshTokens();
  } catch (err) {
    // Error handling is already done in the TokenRefreshQueue
    throw err;
  }
}

/**
 * exported functions
 */

export async function getProfileImage(authState) {
  if (!authState?.identity?.profileFileId) {
    return {};
  }

  if (authState.cookieConsent?.performance) {
    var getCachedData = getLocalStorage("profileImage");
    if (getCachedData) {
      authState.profileImage = JSON.parse(getCachedData);
      return authState.profileImage;
    }
  }

  var newCachedData = {
    fileId: authState.identity?.profileFileId,
    imgData: null
  };

  try {
    var apiFileObject = await getData(
      `/files/${authState.identity?.profileFileId}`,
      {
        queryStringParameters: {
          size: "small"
        }
      }
    );
    if (apiFileObject.url) {
      newCachedData.imgData = await urlToData(apiFileObject.url);
    }
  } catch (err) {
    throw err;
  }

  if (authState.cookieConsent?.performance) {
    setCachedItems("profileImage", JSON.stringify(newCachedData));
  }

  return newCachedData;
}

export async function getLoyaltyPoints(authState) {
  if (
    getEnvironmentVariable("SUPPORTS_LOYALTY_STORE") !== "true" ||
    !authState?.identity?.userId
  ) {
    return {};
  }

  var loyaltyCookie = getLocalStorage(`loyaltyPoints`);
  loyaltyCookie = loyaltyCookie ? JSON.parse(loyaltyCookie) : {};

  if (
    !loyaltyCookie?.cookieDate ||
    (new Date().getTime() - new Date(loyaltyCookie?.cookieDate).getTime()) /
      (1000 * 60) >=
      5
  ) {
    loyaltyCookie = await refreshLoyaltyPoints(authState);
  }

  return (
    loyaltyCookie?.loyaltyPoints || {
      earnt: 0,
      spent: 0,
      adjustments: 0,
      available: 0
    }
  );
}

export async function refreshLoyaltyPoints(authState) {
  if (getEnvironmentVariable("SUPPORTS_LOYALTY_STORE") !== "true") {
    return null;
  }

  let loyaltyPoints = {};
  if (authState?.identity?.userId) {
    var apiUser = await getData(`/users/${authState?.identity?.userId}`, {
      queryStringParameters: {}
    });
    loyaltyPoints = apiUser?.loyaltyPoints || {};
  }

  let loyaltyCookie = setLoyaltyPoints(loyaltyPoints);

  return loyaltyCookie;
}

export function setLoyaltyPoints(loyaltyPoints) {
  if (getEnvironmentVariable("SUPPORTS_LOYALTY_STORE") !== "true") {
    return;
  }
  var loyaltyCookie = {
    cookieDate: new Date(),
    loyaltyPoints: loyaltyPoints
  };

  try {
    setLocalStorage("loyaltyPoints", JSON.stringify(loyaltyCookie));
  } catch (err) {}

  return loyaltyCookie;
}
